<template>
  <v-flex>
    <confirm-dialog
      :is-open="deleteEntryDialog"
      :dialog-header="$t('deleteHeader')"
      :dialog-body="$t('deleteBody')"
      :is-loading="isLoading"
      :confirm-button-text="$t('delete')"
      @confirm="actionEntry({ action: `delete`, salaryGroup: currentSalaryGroupId })"
      @dialogToggle="closeDialog"
    />

    <v-dialog
      v-model="editEntryDialog"
      width="500"
    >
      <v-card>

        <v-card-title>
          <span class="headline">{{ isEditing ? $t('editHeading') : $t('createHeading') }}</span>
        </v-card-title>

        <v-card-text>
          <v-container grid-list-md>
            <v-form
              ref="form"
              v-model="isFormValid"
              @keyup.native.enter="isEditing ? actionEntry({ action: `edit` }) : actionEntry({ action: `create` })"
            >

              <v-text-field
                v-model="salaryGroup.name"
                :error-messages="errorMessages.name"
                :label="$t('name')"
                :rules="[$rules.isRequired]"
              />

              <v-text-field
                v-model="salaryGroup.hourlyWage"
                :error-messages="errorMessages.hourlyWage"
                :label="$t('hourlyWage')"
                :rules="[$rules.isRequired, $rules.isValidFloat]"
                @input="() => { salaryGroup.hourlyWage = salaryGroup.hourlyWage.replace(/,/g, '.') }"
              />

            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer/>
          <v-btn
            flat
            @click.native="closeDialog"
          >
            {{ $t('cancel') }}
          </v-btn>

          <disabled-user-button>
            <v-btn
              slot-scope="{isDisabledForUser}"
              :disabled="isDisabledForUser || isLoading || !isFormValid"
              :loading="isLoading"
              color="primary"
              @click.native="isEditing ? actionEntry({ action: `edit` }) : actionEntry({ action: `create` })"
            >
              {{ isEditing ? $t('edit') : $t('create') }}
            </v-btn>
          </disabled-user-button>
        </v-card-actions>

      </v-card>
    </v-dialog>

    <v-data-table
      :headers="headers"
      :items="salaryGroups"
      :rows-per-page-items="[15]"
      :total-items="paginationTotal"
      :pagination.sync="pagination"
      :loading="isTableLoading"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <td @click="openDialog({ action: `edit`, item: props.item })">{{ props.item.name }}</td>
        <td @click="openDialog({ action: `edit`, item: props.item })">{{ props.item.hourlyWage | toEuro }}</td>
        <td class="text-xs-right">
          <v-btn
            v-if="props.item.company_id !== null"
            icon
            @click.native="openDialog({ action: `delete`, item: props.item })"
          >
            <v-icon>delete</v-icon>
          </v-btn>
        </td>
      </template>
    </v-data-table>

    <v-card-actions>
      <v-spacer/>
      <v-btn
        :disabled="isNoCompanyAndCM || isDialogOpen"
        color="primary"
        @click="openDialog({ action: `create` })"
      >
        {{ $t('add') }}
      </v-btn>
    </v-card-actions>

  </v-flex>
</template>

<script>
import { mapGetters } from 'vuex'
import CompanyApi from '@/api/CompanyApi'
import PaginationApi from '@/api/PaginationApi'
import ConfirmDialog from '@/components/global-components/confirmDialog/ConfirmDialog'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'
import { toEuro } from '@/utils/customFormatters'

export default {
  name: 'CompanySalary',
  components: { DisabledUserButton, ConfirmDialog },
  filters: {
    toEuro
  },
  props: ['companyId'],
  data () {
    return {
      url: `company/salaryGroups`,
      pagination: {},
      paginationTotal: null,
      isLoading: false,
      isFormValid: false,
      isTableLoading: false,
      isEditing: false,
      editEntryDialog: false,
      deleteEntryDialog: false,
      salaryGroups: [],
      currentSalaryGroupId: null,
      errorMessages: {},
      salaryGroup: {},
      headers: [
        { text: this.$t('name'), sortable: false, width: '50%' },
        { text: this.$t('hourlyWage'), sortable: false, width: '50%' },
        { text: '', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters('user', ['getUser']),
    isNoCompanyAndCM () {
      return !this.getUser.has_company && this.getUser.role === 'company_manager'
    },
    isDialogOpen () {
      return this.editEntryDialog || this.deleteEntryDialog
    }
  },
  methods: {
    async getPaginatedData () {
      this.isTableLoading = true
      try {
        const response = await PaginationApi.getPaginatedData(this.url, [`page=${this.pagination.page}`])
        this.paginationTotal = response.total
        this.salaryGroups = response.data
      } catch (e) {
        this.$notification(e)
      }
      this.isTableLoading = false
    },
    openDialog ({ action, item }) {
      if (action === `create`) {
        this.editEntryDialog = true
        this.isEditing = false
      } else if (action === `edit` && item.company_id !== null) {
        this.editEntryDialog = true
        this.salaryGroup = Object.assign({}, item)
        this.isEditing = true
      } else if (action === `delete`) {
        this.currentSalaryGroupId = item.id
        this.deleteEntryDialog = true
      }
    },
    closeDialog () {
      this.editEntryDialog = false
      this.deleteEntryDialog = false
      this.$refs.form.reset()
      this.errorMessages = {}
    },
    async actionEntry ({ action, salaryGroup }) {
      try {
        this.isLoading = true
        await this.actionEntryByRole({ action: action, companyId: this.companyId, salaryGroup: salaryGroup || this.salaryGroup })
        this.closeDialog()
        this.getPaginatedData()
      } catch (e) {
        this.errorMessages = e.errors
      }
      this.isLoading = false
    },
    actionEntryByRole ({ action, companyId, salaryGroup }) {
      return CompanyApi[`${action}SalaryGroup`](salaryGroup)
    }
  },
  watch: {
    pagination: {
      handler: `getPaginatedData`
    },
    salaryGroup: {
      deep: true,
      handler () { this.errorMessages = {} }
    }
  },
  i18n: {
    messages: {
      de: {
        add: 'Neue erstellen',
        cancel: 'Abbrechen',
        create: 'Erstellen',
        createHeading: 'Neue Gruppe anlegen',
        deleteBody: 'Nur Lohngruppen ohne zugewiesene Mitarbeiter können entfernt werden.',
        deleteHeader: 'Lohngruppe entfernen?',
        edit: 'Speichern',
        editHeading: 'Bearbeiten',
        heading: 'Lohngruppen',
        hourlyWage: 'Stundenlohn',
        name: 'Name',
        delete: 'Entfernen'

      },
      en: {
        add: 'New salary group',
        cancel: 'Cancel',
        create: 'Create',
        createHeading: 'Create new group',
        deleteBody: 'Only salary groups without assigned employees may be deleted. ',
        deleteHeader: 'Delete salary group?',
        edit: 'Save',
        editHeading: 'Edit salary group',
        heading: 'Salary groups',
        hourlyWage: 'Hourly wage',
        name: 'Name',
        delete: 'Delete'
      },
      tr: {
        add: 'Yeni ücret grubu',
        cancel: 'İptal',
        create: 'Tanımla',
        createHeading: 'Yeni grup tanımla',
        deleteBody: 'Yalnız çalışan bulunmayan ücret grupları silinebilir. ',
        deleteHeader: 'Ücret grubunu sil?',
        edit: 'Kaydet',
        editHeading: 'Ücret grubunu güncelle',
        heading: 'Ücret grupları',
        hourlyWage: 'Saatlik ücret',
        name: 'İsim',
        delete: 'Sil'
      },
      ru: {
        add: 'Новая группа зарплат',
        cancel: 'Отмена',
        create: 'Создать',
        createHeading: 'Создать новую группу',
        deleteBody: 'Только группы заработной платы без назначенных сотрудников могут быть удалены.',
        deleteHeader: 'Удалить группу зарплат?',
        edit: 'Сохранить',
        editHeading: 'Изменить группу зарплат',
        heading: 'Зарплатные группы',
        hourlyWage: 'Почасовая заработная плата',
        name: 'Имя',
        delete: 'Entfernen'
      }
    }
  }
}
</script>
